import {
    AchTemplateStatusTypeDto,
    GetAchChildSupportTemplateResponseDto,
} from '@treasury/api/channel';
import { DomainEntity } from '../shared';
import { ChildSupportTemplateRecipient } from './child-support-recipient.entity';

export class ChildSupportTemplate extends DomainEntity<GetAchChildSupportTemplateResponseDto> {
    public createDefault(): this {
        return new ChildSupportTemplate({
            id: '',
            name: '',
            achCompanyId: 0,
            achCompanyUniqueId: '',
            achCompanyName: '',
            companyIdentity: '',
            secCode: 'CCD',
            entryDescription: '',
            discretionaryData: '',
            restricted: false,
            statusDescription: '',
            status: AchTemplateStatusTypeDto.Ready,
            lastUpdated: '',
            numberOfApprovalsNeeded: 0,
            completedApprovalCount: 0,
            debitAmount: 0,
            creditAmount: 0,
            recipients: [],
        } as unknown as GetAchChildSupportTemplateResponseDto) as this;
    }

    public get id() {
        // Search response uses 'templateId', GET response uses 'id'
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.dto.id ?? (this.dto as any).templateId;
    }

    public get name() {
        // Search response uses 'templateName', GET response uses 'name'
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.dto.name ?? (this.dto as any).templateName;
    }

    public get status() {
        return this.dto.status;
    }

    public get statusDescription() {
        // GET response has 'statusDescription', search response only has 'status' (as string)
        return this.dto.statusDescription ?? String(this.dto.status ?? '');
    }

    public get achCompanyId() {
        return this.dto.achCompanyId;
    }

    public get achCompanyUniqueId() {
        return this.dto.achCompanyUniqueId;
    }

    public get achCompanyName() {
        return this.dto.achCompanyName ?? '';
    }

    public get companyIdentity() {
        return this.dto.companyIdentity ?? '';
    }

    public get secCode() {
        return this.dto.secCode ?? '';
    }

    public get entryDescription() {
        return this.dto.entryDescription ?? '';
    }

    public get discretionaryData() {
        return this.dto.discretionaryData ?? '';
    }

    public get restricted() {
        return this.dto.restricted ?? false;
    }

    public get lastUpdated() {
        return this.dto.lastUpdated ?? '';
    }

    public get audit() {
        return this.dto.audit;
    }

    public get userPermissions() {
        return this.dto.userPermissions ?? [];
    }

    public get numberOfApprovalsNeeded() {
        return this.dto.numberOfApprovalsNeeded ?? 0;
    }

    public get completedApprovalCount() {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (this.dto as any).completedApprovalCount ?? 0;
    }

    public get debitAmount() {
        return this.dto.debitAmount ?? 0;
    }

    public get creditAmount() {
        return this.dto.creditAmount ?? 0;
    }

    public get recipients(): ChildSupportTemplateRecipient[] {
        return (this.dto.recipients ?? []).map(r => new ChildSupportTemplateRecipient(r));
    }

    public get formattedCreditAmount() {
        return this.dto.creditAmount.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
        });
    }

    public get formattedDebitAmount() {
        return this.dto.debitAmount.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
        });
    }

    public hasPermission(permission: string): boolean {
        return (this.userPermissions as string[]).includes(permission);
    }
}
